import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import API_URL from "../../common/config2"; // Adjust the import path as needed
// Import Step Components
import Step1BasicInfo from "./Step1BasicInfo";
import Step2OtpVerification from "./Step2OtpVerification";
import Step3StartupInfo from "./Step3StartupInfo";
import Step4Payment from "./Step4Payment";
import Step5Success from "./Step5Success";
import { toast } from "react-toastify";

const firebaseConfig = {
  apiKey: "AIzaSyBAMtvrbKK1p7hTG1V2AYz2bpGpGDcUAMQ",
  authDomain: "clifai-cd683.firebaseapp.com",
  projectId: "clifai-cd683",
  storageBucket: "clifai-cd683.firebasestorage.app",
  messagingSenderId: "1001395506359",
  appId: "1:1001395506359:web:a819d14200c1de7aae5132",
  measurementId: "G-YH75BT5LTN",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const provider = new GoogleAuthProvider();

export default function Signup() {
  const navigate = useNavigate();
  const location = useLocation();

  // Default Selected Plan
  const selectedPlan = location.state?.selectedPlan || {
    id: "alpha",
    name: "Alpha",
    price: "Free",
    duration: "30 days",
  };
  console.log("Selected Plan:", selectedPlan);

  // Initial Form State
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    agreeToTerms: false,
    startupId: "",
    startupName: "",
    country: "",
    state: "",
    industry: "Other",
    website: "",
    startupStage: "Growth",
    contactNumber: "",
    elevatorPitch: "",
    selectedPlan: selectedPlan.id || "alpha",
    googleSignIn: false, // New flag to track if user signed up with Google
  });

  // Validation State
  const [stepsValidated, setStepsValidated] = useState({
    step1: false,
    step2: false,
    step3: false,
    step4: false,
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [csrfToken, setCsrfToken] = useState("");
  const [tokenExpired, setTokenExpired] = useState(false);

  // Try to retrieve CSRF token and refresh it if needed
  useEffect(() => {
    const fetchCsrfToken = async (retries = 3, delay = 1000) => {
      setTokenExpired(false);
      for (let i = 0; i < retries; i++) {
        try {
          const response = await axios.get(`${API_URL}/csrf-token`, {
            withCredentials: true,
          });
          setCsrfToken(response.data.csrfToken);
          const token = response.data.csrfToken;
          localStorage.setItem("csrfToken", token);
          return;
        } catch (err) {
          console.error(`CSRF Token Fetch Attempt ${i + 1} Failed:`, err);
          if (i < retries - 1)
            await new Promise((res) => setTimeout(res, delay));
        }
      }
      setErrors((prev) => ({
        ...prev,
        general:
          "Failed to initialize security token. Please refresh the page.",
      }));
    };

    fetchCsrfToken();
  }, [tokenExpired]); // Re-run when tokenExpired changes

  // OTP resend timer logic
  useEffect(() => {
    let timer;
    if (otpResendTimer > 0) {
      timer = setInterval(() => {
        setOtpResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [otpResendTimer]);

  // Prevent moving to next step if current step isn't validated
  useEffect(() => {
    // If trying to access a step that hasn't been validated
    if (
      (currentStep === 2 && !stepsValidated.step1) ||
      (currentStep === 3 && !stepsValidated.step2) ||
      (currentStep === 4 && !stepsValidated.step3) ||
      (currentStep === 5 && !stepsValidated.step4)
    ) {
      // Reset to the highest validated step
      if (stepsValidated.step3) setCurrentStep(4);
      else if (stepsValidated.step2) setCurrentStep(3);
      else if (stepsValidated.step1) setCurrentStep(2);
      else setCurrentStep(1);
    }
  }, [currentStep, stepsValidated]);

  // Auto-validate payment step for free plans
  useEffect(() => {
    // If startup info has been validated and it's a free plan
    if (
      stepsValidated.step3 &&
      (selectedPlan.price === 0 ||
        selectedPlan.price === "0" ||
        selectedPlan.price === "Free")
    ) {
      // Auto-validate payment step
      setStepsValidated((prev) => ({ ...prev, step4: true }));
    }
  }, [stepsValidated.step3, selectedPlan.price]);

  // Step 1: Basic Info Validation
  const validateBasicInfo = () => {
    const newErrors = {};

    if (!formData.firstName || !formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName || !formData.lastName.trim())
      newErrors.lastName = "Last name is required";

    if (!formData.email || !formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    // Skip password validation for Google sign-in
    if (!formData.googleSignIn) {
      if (!formData.password || !formData.password.trim()) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password =
          "Password must contain at least one uppercase letter";
      } else if (!/[0-9]/.test(formData.password)) {
        newErrors.password = "Password must contain at least one number";
      }
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the terms and services";
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;

    if (isValid) {
      setStepsValidated((prev) => ({ ...prev, step1: true }));
    }

    return isValid;
  };

  // Step 3: Startup Info Validation
  const validateStartupInfo = () => {
    const newErrors = {};

    if (!formData.startupName || !formData.startupName.trim())
      newErrors.startupName = "Startup name is required";

    if (!formData.country || !formData.country.trim())
      newErrors.country = "Country is required";
    if (!formData.state || !formData.state.trim())
      newErrors.state = "State is required";

    if (!formData.contactNumber || !formData.contactNumber.trim()) {
      newErrors.contactNumber = "Contact number is required";
    } else if (
      !/^\+?[0-9]{10,15}$/.test(formData.contactNumber.replace(/[\s-]/g, ""))
    ) {
      newErrors.contactNumber = "Contact number is invalid";
    }

    if (!formData.elevatorPitch || !formData.elevatorPitch.trim()) {
      newErrors.elevatorPitch = "Elevator pitch is required";
    } else if (formData.elevatorPitch.length > 200) {
      newErrors.elevatorPitch = "Elevator pitch must be 200 characters or less";
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;

    if (isValid) {
      setStepsValidated((prev) => ({ ...prev, step3: true }));
    }

    return isValid;
  };

  // Handle session expiry error
  const handleSessionExpiredError = () => {
    // Clear existing token
    localStorage.removeItem("csrfToken");
    setCsrfToken("");

    // Show error message
    setErrors({
      general: "Your session has expired. Refreshing your security token...",
    });

    // Request a new token
    setTokenExpired(true);

    // After a brief delay, clear the error message
    setTimeout(() => {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (
          newErrors.general ===
          "Your session has expired. Refreshing your security token..."
        ) {
          delete newErrors.general;
        }
        return newErrors;
      });
    }, 3000);
  };

  // Google Sign-in handler
  const handleGoogleSignup = async () => {
    setIsLoading(true);
    setErrors({});
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      // console.log("Google Sign Up Result:", user.email);

      const checkEmailAlreadyExits = await axios.post(
        `${API_URL}/auth-check`,
        { email: user.email },
        {
          headers: {
            "x-csrf-token": csrfToken,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      // console.log("Email Exists Check:", checkEmailAlreadyExits.data.exists);

      if (checkEmailAlreadyExits.data.exists === true) {
        toast.error("Startup Email already exists.");
        navigate("/Login");
        setIsLoading(false);
        return;
      }
      const googleUser = {
        firstName: user.displayName?.split(" ")[0] || "",
        lastName: user.displayName?.split(" ").slice(1).join(" ") || "",
        email: user.email,
        // Generate a secure random password for backend requirements
        password:
          Math.random().toString(36).slice(-10) +
          Math.random().toString(36).toUpperCase().slice(-2) +
          Math.floor(Math.random() * 10),
        googleSignIn: true,
        agreeToTerms: true, // Auto-accept terms for Google sign-in
      };

      console.log("Google Sign Up successful:", user.email);

      toast.success(
        "Google sign-in successful! Please complete your startup information."
      );

      // Update form data with Google user info
      setFormData((prev) => ({
        ...prev,
        ...googleUser,
      }));

      // Set validation for step 1 and skip OTP verification
      setStepsValidated((prev) => ({ ...prev, step1: true, step2: true }));

      // Skip to Step 3 (Startup Info)
      setCurrentStep(3);

      setMessage(
        "Google sign-in successful! Please complete your startup information."
      );
    } catch (error) {
      console.error("Google Sign Up Error:---------", error);
      toast.error(
        "Error" + error.message || "Google sign-in failed. Please try again."
      );
      setErrors({
        general:
          "Google sign-in failed. Please try again or use email registration.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Send OTP for verification
  const sendOtp = async () => {
    if (!validateBasicInfo()) return;

    // Skip OTP for Google Sign-in
    if (formData.googleSignIn) {
      setStepsValidated((prev) => ({ ...prev, step1: true, step2: true }));
      setCurrentStep(3);
      return;
    }

    if (otpResendTimer > 0) {
      setErrors((prev) => ({
        ...prev,
        general: `Please wait ${otpResendTimer} seconds before resending.`,
      }));
      return;
    }

    if (!csrfToken) {
      setErrors((prev) => ({
        ...prev,
        general: "Session error. Please refresh the page.",
      }));
      return;
    }

    setIsLoading(true);
    setMessage("");
    setErrors({});

    try {
      const otpResponse = await axios.post(
        `${API_URL}/otp/send`,
        { email: formData.email },
        {
          headers: {
            "x-csrf-token": csrfToken,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      if (
        otpResponse.status === 200 ||
        otpResponse.status === 201 ||
        otpResponse.status === 202
      ) {
        setMessage("OTP sent to your email address. Check spam/junk folder.");
        toast.success("OTP sent successfully!");
        setOtpResendTimer(60);
        setCurrentStep(2);
      } else {
        toast.error(
          `Unexpected response: ${otpResponse.status}. Please try again.`
        );
        setErrors((prev) => ({
          ...prev,
          general: "Unexpected response from server. Please try again.",
        }));
      }
    } catch (error) {
      console.error("OTP Send Error:", error.response?.data, error.message);
      toast.error("Failed to send OTP. Please try again later.");

      // Handle session expired error
      if (
        error.response?.status === 403 &&
        (error.response?.data?.error?.includes("CSRF token") ||
          error.response?.data?.error?.includes("session expired"))
      ) {
        handleSessionExpiredError();
        return;
      }

      let errorMessage = "Failed to send OTP. Please try again.";

      if (error.response?.status === 429) {
        errorMessage = "Too many requests. Please wait a minute.";
      } else if (error.response?.status === 400) {
        errorMessage = "Invalid email address.";
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      setErrors((prev) => ({ ...prev, general: errorMessage }));
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP code
  const verifyOtp = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) {
      setOtpError("Please enter the complete 6-digit OTP");
      return;
    }

    setIsLoading(true);
    setOtpError("");

    try {
      const response = await axios.post(
        `${API_URL}/otp/verify`,
        {
          email: formData.email,
          otp: enteredOtp,
        },
        {
          headers: {
            "x-csrf-token": csrfToken,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      console.log("OTP Verify Response:", response.data, response.status);

      if (response.status === 200) {
        setMessage("Email verified successfully!");
        toast.success("OTP verified successfully!");
        setStepsValidated((prev) => ({ ...prev, step2: true }));
        setCurrentStep(3);
      } else {
        setOtpError(
          `Unexpected response: ${response.status}. Please try again.`
        );
      }
    } catch (error) {
      console.error(
        "OTP Verify Error:",
        error.response?.data,
        error.response?.status,
        error.message
      );
      toast.error("Failed to verify OTP. Please try again.");

      // Handle session expired error
      if (
        error.response?.status === 403 &&
        (error.response?.data?.error?.includes("CSRF token") ||
          error.response?.data?.error?.includes("session expired"))
      ) {
        handleSessionExpiredError();
        setOtpError("Session expired. Please try again after page refreshes.");
        return;
      }

      let errorMessage = "Failed to verify OTP. Please try again.";

      if (error.response?.status === 400) {
        errorMessage =
          error.response.data.error || "Invalid OTP. Please try again.";
      }

      setOtpError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Save startup information
  const saveStartupInfo = async (e) => {
    e.preventDefault();
    if (!validateStartupInfo()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const startupData = new FormData();
      startupData.append("firstName", formData.firstName);
      startupData.append("lastName", formData.lastName);
      startupData.append("email", formData.email);
      startupData.append("password", formData.password);
      startupData.append("startupName", formData.startupName);
      startupData.append("country", formData.country);
      startupData.append("state", formData.state);
      startupData.append("industry", formData.industry);
      startupData.append("website", formData.website || "");
      startupData.append("startupStage", formData.startupStage);
      startupData.append("contactNumber", formData.contactNumber);
      startupData.append("elevatorPitch", formData.elevatorPitch);
      startupData.append("googleSignIn", formData.googleSignIn);
      startupData.append("selectedPlan", formData.selectedPlan);
      const response = await axios.post(`${API_URL}/startups`, startupData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-csrf-token": csrfToken,
        },
        withCredentials: true,
      });

      console.log("Startup Save Response:", response.data);

      if (response.status === 201 || response.status === 200) {
        setMessage("Startup information saved successfully!");
        toast.success("Startup information saved successfully!");
        localStorage.setItem("startUpData", response.data.startup);
        localStorage.setItem("startUpId", response.data.startupId);
        localStorage.setItem("token", response.data.token);
        setFormData((prev) => ({
          ...prev,
          startupId: response.data.startupId || response.data._id || "",
        }));
        setStepsValidated((prev) => ({ ...prev, step3: true }));
        setCurrentStep(4);
      } else {
        setErrors({
          general: "Unexpected response from server. Please try again.",
        });
      }
    } catch (error) {
      console.error("Startup Save Error:", error.response?.data, error.message);
      toast.error("Failed to save startup information. Please try again.");

      // Handle session expired error
      if (
        error.response?.status === 403 &&
        (error.response?.data?.error?.includes("CSRF token") ||
          error.response?.data?.error?.includes("session expired"))
      ) {
        handleSessionExpiredError();
        return;
      }

      let errorMessage =
        "Failed to save startup information. Please try again.";

      if (error.response?.status === 400) {
        errorMessage =
          error.response.data.error ||
          "Invalid startup data. Please check your inputs.";
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle payment and finish signup
  const handlePaymentComplete = async (paymentResult) => {
    setIsLoading(true);
  };

  // Handle step navigation
  const goToStep = (step) => {
    // Only allow going to steps that have been validated
    if (
      step === 1 ||
      (step === 2 && stepsValidated.step1) ||
      (step === 3 && stepsValidated.step2) ||
      (step === 4 && stepsValidated.step3) ||
      (step === 5 && stepsValidated.step4)
    ) {
      setCurrentStep(step);
    } else {
      // If trying to access an invalid step, show message
      setErrors({
        general: "Please complete the current step before proceeding.",
      });
    }
  };

  return (
    <section
      className="contact-us mega-section"
      id="signup-section"
      style={{ backgroundColor: "#060922", minHeight: "100vh" }}>
      <div className="container">
        <div className="row justify-content-center">
          <div
            className="col-md-12 col-lg-12 col-xl-8"
            style={{ paddingTop: "100px" }}>
            <div className="signup-progress ">
              <div
                className="progress"
                style={{
                  height: "8px",
                  backgroundColor: "rgba(255,255,255,0.1)",
                }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{
                    width: `${(currentStep / 5) * 100}%`,
                    backgroundColor: "#09aff4",
                  }}
                  aria-valuenow={currentStep}
                  aria-valuemin="0"
                  aria-valuemax="5"></div>
              </div>
              <div className="d-flex justify-content-between mt-2">
                <span
                  className={`step-indicator ${
                    currentStep >= 1 ? "active" : ""
                  }`}
                  style={{
                    cursor: "pointer",
                    color: currentStep >= 1 ? "#09aff4" : "#fff",
                    fontWeight: currentStep === 1 ? "bold" : "normal",
                  }}>
                  Basic Info
                </span>
                <span
                  className={`step-indicator ${
                    currentStep >= 2 ? "active" : ""
                  }`}
                  style={{
                    cursor: stepsValidated.step1 ? "pointer" : "not-allowed",
                    color: currentStep >= 2 ? "#09aff4" : "#fff",
                    opacity: stepsValidated.step1 ? 1 : 0.5,
                    fontWeight: currentStep === 2 ? "bold" : "normal",
                  }}>
                  {/* Verify */}
                </span>
                <span
                  className={`step-indicator ${
                    currentStep >= 3 ? "active" : ""
                  }`}
                  style={{
                    cursor: stepsValidated.step2 ? "pointer" : "not-allowed",
                    color: currentStep >= 3 ? "#09aff4" : "#fff",
                    opacity: stepsValidated.step2 ? 1 : 0.5,
                    fontWeight: currentStep === 3 ? "bold" : "normal",
                  }}>
                  Startup
                </span>
                <span
                  className={`step-indicator ${
                    currentStep >= 4 ? "active" : ""
                  }`}
                  style={{
                    cursor: stepsValidated.step3 ? "pointer" : "not-allowed",
                    color: currentStep >= 4 ? "#09aff4" : "#fff",
                    opacity: stepsValidated.step3 ? 1 : 0.5,
                    fontWeight: currentStep === 4 ? "bold" : "normal",
                  }}>
                  Payment
                </span>
                <span
                  className={`step-indicator ${
                    currentStep >= 5 ? "active" : ""
                  }`}
                  style={{
                    cursor: stepsValidated.step4 ? "pointer" : "not-allowed",
                    color: currentStep >= 5 ? "#09aff4" : "#fff",
                    opacity: stepsValidated.step4 ? 1 : 0.5,
                    fontWeight: currentStep === 5 ? "bold" : "normal",
                  }}
                  onClick={() => goToStep(5)}>
                  Complete
                </span>
              </div>
            </div>
            <div
              className="contact-form-panel"
              style={{
                marginTop: "20px",

                boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                borderRadius: "8px",
                overflow: "hidden",
              }}>
              {message && (
                <div
                  className="done-msg"
                  style={{
                    padding: "12px 20px",
                    backgroundColor: "#d4edda",
                    color: "#155724",
                    marginBottom: "20px",
                  }}>
                  {message}
                </div>
              )}
              {errors.general && (
                <div
                  className="done-msg error"
                  style={{
                    padding: "12px 20px",
                    backgroundColor: "#f8d7da",
                    color: "#721c24",
                    marginBottom: "20px",
                  }}>
                  {errors.general}
                </div>
              )}
              {currentStep === 1 && (
                <Step1BasicInfo
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  setErrors={setErrors}
                  isLoading={isLoading}
                  onSubmit={sendOtp}
                  csfrToken={csrfToken}
                  validateBasicInfo={validateBasicInfo}
                  onGoogleSignup={handleGoogleSignup}
                />
              )}
              {currentStep === 2 && (
                <Step2OtpVerification
                  formData={formData}
                  otp={otp}
                  setOtp={setOtp}
                  otpError={otpError}
                  setOtpError={setOtpError}
                  isLoading={isLoading}
                  onVerify={verifyOtp}
                  onResend={sendOtp}
                  onBack={() => goToStep(1)}
                  otpResendTimer={otpResendTimer}
                />
              )}
              {currentStep === 3 && (
                <Step3StartupInfo
                  formData={formData}
                  setFormData={setFormData}
                  // setLogoFile={setLogoFile}
                  setLogoPreview={setLogoPreview}
                  errors={errors}
                  isLoading={isLoading}
                  onSubmit={saveStartupInfo}
                  validateStartupInfo={validateStartupInfo}
                />
              )}
              {currentStep === 4 && (
                <Step4Payment
                  formData={formData}
                  selectedPlan={selectedPlan}
                  isLoading={isLoading}
                  onPaymentComplete={handlePaymentComplete}
                  paymentError={errors.general}
                  csrfToken={csrfToken}
                  onSessionExpired={handleSessionExpiredError}
                  setCurrentStep={setCurrentStep}
                  setIsLoading={setIsLoading}
                  setMessage={setMessage}
                  setStepsValidated={setStepsValidated}
                  setErrors={setErrors}
                  errors={errors}
                />
              )}
              {currentStep === 5 && <Step5Success />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
